import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import GuestLayout from '../components/GuestLayout';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Plus, Minus, Loader2, Check, Calendar, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ServiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { token } = useAuth();
  const [service, setService] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/services/${id}`).then(res => setService(res.data)).catch(console.error);
  }, [id]);

  const handleAddToCart = async () => {
    if (!token) { navigate('/login'); return; }
    setLoading(true);
    const success = await addToCart('service', service.id, quantity, {
      selected_date: selectedDate || null,
      selected_time: selectedTime || null
    });
    setLoading(false);
    if (success) {
      setAdded(true);
      toast.success('Added to cart');
      setTimeout(() => setAdded(false), 2000);
    }
  };

  if (!service) return (
    <GuestLayout>
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-espresso border-t-transparent rounded-full animate-spin" />
      </div>
    </GuestLayout>
  );

  return (
    <GuestLayout>
      <div className="max-w-md mx-auto md:max-w-2xl">
        {/* Hero */}
        <div className="relative aspect-[4/3] overflow-hidden">
          <img src={service.image_url} alt={service.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-espresso/50 via-transparent to-espresso/20" />
          <button
            data-testid="service-back-btn"
            onClick={() => navigate(-1)}
            className="absolute top-5 left-5 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center"
          >
            <ArrowLeft size={18} className="text-espresso" />
          </button>
        </div>

        <div className="px-6 py-6">
          <div className="flex items-start justify-between mb-2">
            <h1 data-testid="service-title" className="font-heading text-2xl text-espresso">{service.title}</h1>
            <span className="text-espresso font-semibold text-lg">${service.price.toFixed(2)}</span>
          </div>
          <span className="inline-block text-xs uppercase tracking-widest text-text-muted font-semibold bg-sand px-3 py-1 rounded-full mb-4">
            {service.category}
          </span>
          <p className="text-text-muted leading-relaxed mb-6">{service.description}</p>

          {/* Date/Time selectors */}
          {service.requires_date && (
            <div className="mb-4">
              <label className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-2 flex items-center gap-2">
                <Calendar size={14} /> Preferred Date
              </label>
              <Input
                data-testid="service-date-input"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="bg-white border border-warm-border rounded-xl"
              />
            </div>
          )}
          {service.requires_time && (
            <div className="mb-4">
              <label className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-2 flex items-center gap-2">
                <Clock size={14} /> Preferred Time
              </label>
              <Input
                data-testid="service-time-input"
                type="time"
                value={selectedTime}
                onChange={(e) => setSelectedTime(e.target.value)}
                className="bg-white border border-warm-border rounded-xl"
              />
            </div>
          )}

          {/* Quantity */}
          <div className="flex items-center gap-4 mb-8">
            <span className="text-xs uppercase tracking-widest text-text-muted font-semibold">Quantity</span>
            <div className="flex items-center gap-3 bg-sand rounded-full px-1 py-1">
              <button
                data-testid="service-qty-minus"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-espresso"
              >
                <Minus size={14} />
              </button>
              <span data-testid="service-qty-value" className="text-espresso font-semibold w-6 text-center">{quantity}</span>
              <button
                data-testid="service-qty-plus"
                onClick={() => setQuantity(quantity + 1)}
                className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-espresso"
              >
                <Plus size={14} />
              </button>
            </div>
          </div>

          {/* Add to Cart */}
          <Button
            data-testid="service-add-to-cart-btn"
            onClick={handleAddToCart}
            disabled={loading || added}
            className="w-full bg-espresso hover:bg-espresso-light text-white rounded-full h-14 text-base font-medium transition-transform active:scale-95"
          >
            {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : added ? <Check className="mr-2" size={18} /> : null}
            {added ? 'Added' : `Add to Cart - $${(service.price * quantity).toFixed(2)}`}
          </Button>
        </div>
      </div>
    </GuestLayout>
  );
}
